// Use Unicode escape for degree symbol to avoid encoding issues
const DEGREE = '\u00B0';

// Weather icon mapping
export const getWeatherIcon = (iconCode, size = '2x') => {
  if (!iconCode) return '';
  return `https://openweathermap.org/img/wn/${iconCode}@${size}.png`;
};

// Format temperature with proper degree symbol
export const formatTemp = (temp, unit = 'F') => {
  if (temp === undefined || temp === null || isNaN(temp)) {
    return `--${DEGREE}${unit}`;
  }
  return `${Math.round(temp)}${DEGREE}${unit}`;
};

// Format temperature without unit (just the number and degree)
export const formatTempShort = (temp) => {
  if (temp === undefined || temp === null || isNaN(temp)) {
    return `--${DEGREE}`;
  }
  return `${Math.round(temp)}${DEGREE}`;
};

// Get wind direction from degrees
export const getWindDirection = (degrees) => {
  if (degrees === undefined || degrees === null || isNaN(degrees)) {
    return 'N/A';
  }
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                      'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
};

// Format time
export const formatTime = (dateString, options = {}) => {
  if (!dateString) return '--:--';
  try {
    const defaultOptions = { hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleTimeString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return '--:--';
  }
};

// Format date
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  try {
    // Extract YYYY-MM-DD from ISO string to avoid timezone shift
    // (e.g. "2026-06-11T00:00:00Z" at UTC-4 would show as Jun 10 otherwise)
    const isoMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    let date;
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      date = new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0);
    } else {
      date = new Date(dateString);
    }
    const defaultOptions = { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
  } catch (e) {
    return 'N/A';
  }
};

// Get the local date string (YYYY-MM-DD) for a given Date object.
// This ensures we compare dates in the user's local timezone, not UTC.
const toLocalDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Get day name, comparing in the user's local timezone.
// The API sends dates as UTC midnight timestamps (e.g. "2026-03-11T00:00:00Z").
// We extract just the date portion (YYYY-MM-DD) and compare it against the
// local today/tomorrow so that timezone offsets don't shift the day label.
export const getDayName = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    // Extract the YYYY-MM-DD portion from the ISO string to avoid timezone shift.
    // If the dateString is an ISO format like "2026-03-12T00:00:00+00:00" or
    // "2026-03-12T00:00:00Z", we take the date part directly.
    // Otherwise fall back to parsing with Date.
    const isoMatch = String(dateString).match(/^(\d{4}-\d{2}-\d{2})/);
    const forecastDateStr = isoMatch
      ? isoMatch[1]
      : toLocalDateString(new Date(dateString));

    const now = new Date();
    const todayStr = toLocalDateString(now);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = toLocalDateString(tomorrow);

    if (forecastDateStr === todayStr) return 'Today';
    if (forecastDateStr === tomorrowStr) return 'Tomorrow';

    // Parse the date string as local (noon to avoid DST edge) for weekday name
    const [y, m, d] = forecastDateStr.split('-').map(Number);
    const localDate = new Date(y, m - 1, d, 12, 0, 0);
    return localDate.toLocaleDateString('en-US', { weekday: 'short' });
  } catch (e) {
    return 'N/A';
  }
};

// Filter forecast days to start from today (user's local date) and return
// at most `count` days. This prevents yesterday from appearing due to
// UTC vs local timezone mismatches.
export const filterForecastDays = (days, count = 5) => {
  if (!days || days.length === 0) return [];

  const now = new Date();
  const todayStr = toLocalDateString(now);

  const filtered = days.filter((day) => {
    // Extract YYYY-MM-DD from the forecast date (sent as UTC midnight ISO string)
    const isoMatch = String(day.date).match(/^(\d{4}-\d{2}-\d{2})/);
    const dayDateStr = isoMatch
      ? isoMatch[1]
      : toLocalDateString(new Date(day.date));
    // Keep only days that are today or in the future
    return dayDateStr >= todayStr;
  });

  return filtered.slice(0, count);
};

// Get alert color
export const getAlertColor = (severity) => {
  const colors = {
    'Extreme': 'var(--alert-extreme)',
    'Severe': 'var(--alert-severe)',
    'Moderate': 'var(--alert-moderate)',
    'Minor': 'var(--alert-minor)',
    'Unknown': 'var(--text-muted)'
  };
  return colors[severity] || colors['Unknown'];
};

// Export degree symbol for use in other components
export const DEGREE_SYMBOL = DEGREE;